import * as PIXI from "pixi.js";
import {Container} from "../core/Container";
import {IPoint} from "../core/Geom";
import {Callback, list} from "../core/Reflection";
import {Times} from "../core/Signal";
import {AnyStorySceneModel, OptionId, SlotId} from "../data/StoryModel";
import {AbstractStoryScene} from "./AbstractStoryScene";
import {AnimationContext} from "./animations/AnimationContext";
import {InteractiveSceneItem} from "./InteractiveSceneItem";
import {Optionable, OptionableData} from "./Optionable";
import {PaperLayer} from "./PaperLayer";
import {SceneItemMask} from "./SceneItemMask";
import {StorySceneContainer} from "./StorySceneContainer";

export class StoryScene extends AbstractStoryScene {

    public readonly container = this.addChildAt(new StorySceneContainer(), 0);
    public readonly topContainer = this.addChild(new Container());
    public readonly basePaperLayer = this.container.addChild(new PaperLayer());
    public readonly items: Optionable[] = list((i: number, p: OptionableData | undefined) =>
        this.container.addChild(p && p.isPaperLayer ?
            new PaperLayer() :
            p && p.maskOf ?
                new SceneItemMask() :
                this.createSceneItem(p)));

    constructor(scene: AnyStorySceneModel, size: IPoint, cloneSlots?: boolean) {
        super();

        if (cloneSlots) {
            this.cloneSlots();
        }

        scene.layout.call(this);
        this.container.scale.set(size.y / 1536);
        this.container.y = -size.y * 0.5;
        this.container.x = -size.y * 0.5 * 4 / 3;

        this.topContainer.scale.set(this.container.scale.x);
        this.topContainer.x = this.container.x;
        this.topContainer.y = this.container.y;

        this.caption.text = this.getSceneCaption(scene, false);
        this.setCaptionPosition(size);

        this.updateOptions();
    }

    public setScale(value: number) {
        this.scale.set(value);
        this.basePaperLayer.updateFilterScale(value);
        for (const item of this.items) {
            if (item.updateFilterScale) {
                item.updateFilterScale(value);
            }
        }
    }

    public updateOptions() {
        for (const item of this.items) {
            this.updateItem(item);
        }
        this.initPaperCuts(this.items);
    }

    /**
     * Returns the list of all options in the scene in format "optionId" | "optionId:slotId"
     */
    public getSceneOptions(): string[] {
        return this.collectFromItems((item: Optionable, options: string[]) => {
            if (item.option) {
                for (const option of item.option.split(/\s+/ig)) {
                    if (options.indexOf(option) < 0) {
                        options.push(option);
                    }
                }
            }
        });
    }

    public getSceneAssets(): string[] {
        return this.collectFromItems((item: Optionable, assets: string[]) => {
            if (item.textureName && item.getVisible()) {
                assets.push(item.textureName);
            }
        });
    }

    public collectFromItems<T>(collect: (it: Optionable, items: T[]) => void): T[] {
        const values: T[] = [];
        for (const item of this.items) {
            this._collectFromItems(collect, item, values);
        }
        return values;
    }

    public animate(cb: Callback) {
        const animatable = this.items.filter((it) => !!it.animate);
        const context: AnimationContext = {
            scene: this,
        };
        if (animatable.length > 0) {
            const onCompleteChildren = Times(animatable.length, cb);
            for (const item of animatable) {
                item.animate!(context, onCompleteChildren);
            }
        } else {
            cb();
        }
    }

    protected createSceneItem(data?: OptionableData): Optionable {
        return new InteractiveSceneItem();
    }

    private _collectFromItems<T>(collect: (it: Optionable, items: T[]) => void, item: Optionable, values: T[]) {
        collect(item, values);
        if (item.items) {
            for (const i of item.items) {
                this._collectFromItems(collect, i, values);
            }
        }
    }

    private updateItem(item: Optionable) {
        if (item.option) {
            const options = item.option.split(/\s+/ig);
            item.setVisible(options.every((option) => {
                const optionAndSlot = option.split(":");
                return this.isOptionSelected(optionAndSlot[0] as OptionId, optionAndSlot[1] as SlotId);
            }));
        }
        if (item.items) {
            for (const i of item.items) {
                this.updateItem(i);
            }
        }
    }

    private initPaperCuts(items: Optionable[]) {
        for (const item of items) {
            if (item.getPaperCut && item.getVisible()) {
                const paperCut = item.getPaperCut();
                if (paperCut) {
                    paperCut.changeParent(this.getPaperLayer(item));
                }
            }
            if (item.items) {
                this.initPaperCuts(item.items);
            }
        }
    }

    private getPaperLayer(item: Optionable) {
        let p: PIXI.Container = item;
        while (p.parent && p.parent !== this.container) {
            p = p.parent;
        }
        if (p) {
            for (let i = this.items.indexOf(p as any) - 1; i >= 0; i--) {
                if (this.items[i].isPaperLayer) {
                    return this.items[i];
                }
            }
        }
        return this.basePaperLayer;
    }
}
